import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "@/store";
import { setForgotPasswordToken, setLoading } from "@/store/slices/authSlice";
import actionsHandler from "@/store/actions/actionHandler";
import { OtpInput } from "@/pages/OtpInput";

const { unAuthenticatedCall: verifyEmailOtp } =
  actionsHandler("verifyEmailOtp");
const { unAuthenticatedCall: resendEmailOTP } =
  actionsHandler("resendEmailOTP");

const EmailVerification = () => {
  const { t } = useTranslation("auth");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { tempEmail, loading, forgotPasswordToken } = useSelector(
    (state: RootState) => state.auth,
  );
  const [timer, setTimer] = useState(60); // 10 minutes in seconds
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const validationSchema = Yup.object({
    otp: Yup.string()
      .required(t("emailVerification.form.otp.validation.required"))
      .length(6, t("emailVerification.form.otp.validation.length")),
  });

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        dispatch(setLoading(true));
        const response = await dispatch(
          verifyEmailOtp({
            payload: {
              otp: values.otp,
              token: forgotPasswordToken,
            },
            customParams: ["forgot-password/"],
            method: "post",
          }),
        );

        if (response.ok) {
          toast.success(
            response.data.message ||
              t("emailVerification.messages.verificationSuccess"),
          );
          dispatch(setForgotPasswordToken(response.data.token));
          // Redirect to reset password page after successful verification
          navigate("/reset-password");
        }
      } catch (error) {
        toast.error(t("emailVerification.messages.unexpectedError"));
      } finally {
        dispatch(setLoading(false));
      }
    },
  });

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timer > 0) {
      setIsResendDisabled(true);
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
      if (interval) {
        clearInterval(interval);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timer]);

  const handleResendOTP = async () => {
    try {
      const response = await dispatch(
        resendEmailOTP({
          payload: { email: tempEmail },
          method: "post",
        }),
      );

      if (response.ok) {
        dispatch(setForgotPasswordToken(response.data.token));
        setTimer(600); // Reset timer to 10 minutes after successful resend
        toast.success(t("emailVerification.messages.resendSuccess"));
      }
    } catch (error) {
      toast.error(t("emailVerification.messages.unexpectedError"));
    }
  };

  useEffect(() => {
    if (!tempEmail) {
      navigate("/forget-password");
    }
  }, [tempEmail]);

  // Function to format the timer
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes < 10 ? "0" : ""}${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <div className="tw-min-h-screen tw-flex">
      {/* Left Section */}
      <div className="lg:tw-w-3/4 md:tw-w-2/4 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center sm:tw-flex tw-items-end tw-justify-start tw-text-white tw-p-14">
        <div className="tw-px-4">
          <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
            Innovative Collateral & <br /> Risk Solutions
          </h1>
        </div>
      </div>

      {/* Right Section */}
      <div className="lg:tw-w-1/3 md:tw-w-2/4 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-full tw-max-w-md tw-px-16">
          {/* Logo and Instructions */}
          <div className="tw-mb-8">
            <img
              src="/logo/Logo.svg"
              alt="Logo"
              className="tw-mx-auto tw-max-w-[150px] tw-mb-14"
            />
            <h6 className="font-primary font-primary-color tw-text-[22px] font-weight-semibold tw-leading-6">
              {t("emailVerification.title")}
            </h6>
            <p className="tw-mt-2 tw-leading-6 font-size-sm font-gray-color font-weight-medium">
              {t("emailVerification.subtitle")}
            </p>
          </div>

          {/* OTP Input */}
          <form onSubmit={formik.handleSubmit} className="tw-mb-6">
            <div className="tw-mb-6">
              <OtpInput
                numInputs={6}
                value={formik.values.otp}
                onChange={(value) => formik.setFieldValue("otp", value)}
                hasError={formik.touched.otp && Boolean(formik.errors.otp)}
                isDisabled={loading}
                autoFocus
                placeholder={"*"}
                className="tw-grid tw-grid-cols-6 tw-gap-2"
              />
              {formik.touched.otp && formik.errors.otp && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                  {formik.errors.otp}
                </div>
              )}
            </div>

            {/* Verify Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              className="primary-custom-button tw-py-2"
            >
              {loading
                ? t("emailVerification.form.button.loading")
                : t("emailVerification.form.button.verify")}
            </Button>
          </form>

          {/* Resend Link */}
          <div className="tw-text-center tw-mt-4">
            <Typography
              variant="body2"
              className="font-gray-color font-primary font-size-sm tw-leading-6"
            >
              {t("emailVerification.resend.text")}{" "}
              <span className="tw-font-semibold tw-text-[#000]">
                {formatTime(timer)}{" "}
              </span>
              <button
                onClick={handleResendOTP}
                className={`tw-text-sm tw-text-capitalize tw-font-semibold tw-text-[#146C94] ${timer > 0 ? "tw-opacity-50" : ""}`}
                disabled={timer > 0}
              >
                {t("emailVerification.resend.link")}
              </button>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
